import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownUp,
  BedDouble,
  Building,
  Building2,
  ChevronDown,
  ChevronRight,
  DoorOpen,
  FileBarChart,
  Home,
  Layers,
  LayoutGrid,
  MapPin,
  MonitorSmartphone,
  MoreHorizontal,
  Package as PackageIcon,
  Plus,
  Search,
  Trash2,
  Trees,
  Upload,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, type Assignment, type Device, type Room, type Site, type Zone } from '../api/client';
import { useSession } from '../app/session';
import { ask } from '../components/Confirm';
import { Menu } from '../components/Menu';
import { Pager, usePaged } from '../components/Pager';
import { Select } from '../components/Select';
import { Empty, ErrorBox, Field, Loading, Modal, PageHeader } from '../components/ui';
import { AssignmentEditor } from './PackagesPage';

const TIMEZONES = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf(k: string): string[] }).supportedValuesOf('timeZone');
  } catch {
    return ['UTC', 'Asia/Qatar', 'Asia/Dubai', 'Europe/Paris', 'Africa/Tunis'];
  }
})();

const ROOM_KIND: Record<string, string> = { GUEST_ROOM: 'Guest room', SUITE: 'Suite', LOBBY: 'Lobby', PUBLIC: 'Public area', STAFF: 'Staff', OTHER: 'Other' };

function StatCard({ icon, value, label, sub }: { icon: ReactNode; value: number | string; label: string; sub: string }) {
  return (
    <div className="stat-tile">
      <span className="stat-tile-icon violet">{icon}</span>
      <span>
        <span className="stat-tile-label">{label}</span>
        <span className="stat-tile-value">{value}</span>
        <span className="stat-tile-sub">{sub}</span>
      </span>
    </div>
  );
}

export function SitesPage() {
  const { site, sites, setSiteId, can } = useSession();
  const [creating, setCreating] = useState(false);
  return (
    <>
      <PageHeader
        title="Sites & Rooms"
        sub="Organization → Site → Building / Floor / Area → Room → Device. The most specific package assignment wins."
        actions={
          can('ADMIN') && (
            <button className="btn primary" onClick={() => setCreating(true)}>
              <Plus size={16} /> New site
            </button>
          )
        }
      />
      {sites.length > 1 && (
        <div className="tabs">
          {sites.map((s) => (
            <button key={s.id} className={`tab${s.id === site?.id ? ' active' : ''}`} onClick={() => setSiteId(s.id)}>
              {s.name}
            </button>
          ))}
        </div>
      )}
      {site ? <SiteDetail site={site} sitesCount={sites.length} /> : <div className="card empty">No sites yet.</div>}
      {creating && <CreateSiteModal onClose={() => setCreating(false)} onCreated={(s) => setSiteId(s.id)} />}
    </>
  );
}

function CreateSiteModal({ onClose, onCreated }: { onClose: () => void; onCreated: (s: Site) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const create = useMutation({
    mutationFn: () => api.post<Site>('/sites', { name, code, timezone, defaultLanguage: 'en' }),
    onSuccess: async (s) => {
      await qc.invalidateQueries({ queryKey: ['sites'] });
      onCreated(s);
      onClose();
    },
  });
  return (
    <Modal
      title="New site"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!name || !code || create.isPending} onClick={() => create.mutate()}>
            Create site
          </button>
        </>
      }
    >
      <Field label="Name *">
        <input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!code || code === autoCode(name)) setCode(autoCode(e.target.value));
          }}
          placeholder="Serenity Hotel & Resort"
          autoFocus
        />
      </Field>
      <Field label="Code *" hint="Short unique code, A–Z 0–9 - _">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="SERENITY" />
      </Field>
      <Field label="Time zone" hint="Drives the clock on the TVs.">
        <Select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
          {TIMEZONES.map((tz) => (
            <option key={tz}>{tz}</option>
          ))}
        </Select>
      </Field>
      <ErrorBox error={create.error} />
    </Modal>
  );
}

const autoCode = (name: string) =>
  name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 32);

const zoneIcon = (k: Zone['kind'], size = 16) => (k === 'BUILDING' ? <Building2 size={size} /> : k === 'FLOOR' ? <Building size={size} /> : <Trees size={size} />);

type SortKey = 'number' | 'kind' | 'devices' | 'status';
type RoomState = 'online' | 'offline' | 'none';

function SiteDetail({ site, sitesCount }: { site: Site; sitesCount: number }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const zones = useQuery({ queryKey: ['zones', site.id], queryFn: () => api.get<{ items: Zone[] }>(`/sites/${site.id}/zones`) });
  const rooms = useQuery({ queryKey: ['rooms', site.id, null], queryFn: () => api.get<{ items: Room[] }>(`/sites/${site.id}/rooms`) });
  const devices = useQuery({ queryKey: ['devices', site.id], queryFn: () => api.get<{ items: Device[] }>(`/sites/${site.id}/devices`) });
  const assignments = useQuery({ queryKey: ['assignments', site.id, 'ROOM'], queryFn: () => api.get<{ items: Assignment[] }>(`/sites/${site.id}/assignments?scopeType=ROOM`) });
  const [zoneId, setZoneId] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('all');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'number', dir: 1 });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [newZone, setNewZone] = useState<{ parentId: string | null } | null>(null);
  const [bulk, setBulk] = useState(false);
  const [assignFor, setAssignFor] = useState<{ type: 'SITE' | 'ZONE' | 'ROOM'; id: string; label: string } | null>(null);
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['zones', site.id] });
    void qc.invalidateQueries({ queryKey: ['rooms', site.id] });
    void qc.invalidateQueries({ queryKey: ['sites'] });
    void qc.invalidateQueries({ queryKey: ['assignments'] });
  };
  const deleteZone = useMutation({ mutationFn: (id: string) => api.delete(`/zones/${id}`), onSuccess: invalidate });
  const deleteRooms = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await api.delete(`/rooms/${id}`);
    },
    onSuccess: () => {
      setSelected(new Set());
      invalidate();
    },
  });

  const allZones = zones.data?.items ?? [];
  const allRooms = rooms.data?.items ?? [];
  const children = (pid: string | null) => allZones.filter((z) => z.parentId === pid).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, undefined, { numeric: true }));
  const below = useMemo(() => {
    const m = new Map<string, Set<string>>();
    const collect = (id: string): Set<string> => {
      if (m.has(id)) return m.get(id)!;
      const s = new Set([id]);
      allZones.filter((z) => z.parentId === id).forEach((c) => collect(c.id).forEach((x) => s.add(x)));
      m.set(id, s);
      return s;
    };
    allZones.forEach((z) => collect(z.id));
    return m;
  }, [allZones]);
  const roomsIn = (id: string) => allRooms.filter((r) => r.zoneId && below.get(id)?.has(r.zoneId)).length;
  const zone = allZones.find((z) => z.id === zoneId);

  const stateOf = useMemo(() => {
    const m = new Map<string, RoomState>();
    for (const d of devices.data?.items ?? []) {
      if (!d.roomId) continue;
      if (d.online && d.status === 'ACTIVE') m.set(d.roomId, 'online');
      else if (!m.has(d.roomId)) m.set(d.roomId, 'offline');
    }
    return (r: Room): RoomState => m.get(r.id) ?? 'none';
  }, [devices.data]);
  const pkgsOf = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const a of assignments.data?.items ?? []) m.set(a.scopeId, [...(m.get(a.scopeId) ?? []), a.package.name]);
    return (r: Room) => m.get(r.id) ?? [];
  }, [assignments.data]);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    const order: Record<RoomState, number> = { online: 0, offline: 1, none: 2 };
    return allRooms
      .filter((r) => (zoneId ? !!r.zoneId && !!below.get(zoneId)?.has(r.zoneId) : true))
      .filter((r) => !s || r.number.toLowerCase().includes(s))
      .filter((r) => kind === 'all' || r.kind === kind)
      .sort((a, b) => {
        const k = sort.key;
        const v =
          k === 'kind'
            ? a.kind.localeCompare(b.kind)
            : k === 'devices'
              ? (a._count?.devices ?? 0) - (b._count?.devices ?? 0)
              : k === 'status'
                ? order[stateOf(a)] - order[stateOf(b)]
                : 0;
        return (v || a.number.localeCompare(b.number, undefined, { numeric: true })) * sort.dir;
      });
  }, [allRooms, zoneId, below, q, kind, sort, stateOf]);
  const { rows, pager } = usePaged(shown, 10);
  const pageIds = rows.map((r) => r.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const admin = can('ADMIN');
  const SortTh = ({ k, children: label }: { k: SortKey; children: ReactNode }) => (
    <th className="sortable" onClick={() => setSort((cur) => ({ key: k, dir: cur.key === k ? (-cur.dir as 1 | -1) : 1 }))}>
      {label} <ArrowDownUp size={12} className={sort.key === k ? 'sort-on' : ''} />
    </th>
  );

  const tree = (parentId: string | null, depth: number): ReactNode =>
    children(parentId).map((z) => {
      const kids = children(z.id);
      const expanded = open.has(z.id);
      return (
        <div key={z.id}>
          <div className={`tree-row${zoneId === z.id ? ' on' : ''}`} style={{ paddingLeft: 8 + depth * 18 }} onClick={() => setZoneId(z.id)}>
            <button
              type="button"
              className="tree-caret"
              style={{ visibility: kids.length ? 'visible' : 'hidden' }}
              onClick={(e) => {
                e.stopPropagation();
                setOpen((cur) => {
                  const n = new Set(cur);
                  if (n.has(z.id)) n.delete(z.id);
                  else n.add(z.id);
                  return n;
                });
              }}
            >
              {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            </button>
            <span className="tree-icon">{zoneIcon(z.kind)}</span>
            <span className="tree-name">{z.name}</span>
            <span className="pkg-count">{roomsIn(z.id)}</span>
            {admin && (
              <Menu
                minWidth={200}
                button={({ onClick }) => (
                  <button type="button" className="icon-btn tree-more" title="More" onClick={onClick}>
                    <MoreHorizontal size={16} />
                  </button>
                )}
              >
                <button type="button" className="more-item" onClick={() => setNewZone({ parentId: z.id })}>
                  <Plus size={15} /> Add inside
                </button>
                <button type="button" className="more-item" onClick={() => setAssignFor({ type: 'ZONE', id: z.id, label: z.name })}>
                  <PackageIcon size={15} /> Packages
                </button>
                <button type="button" className="more-item danger-item" onClick={() => ask(`Delete ${z.name}? Its rooms stay, without a building or floor.`, () => deleteZone.mutate(z.id))}>
                  <Trash2 size={15} /> Delete
                </button>
              </Menu>
            )}
          </div>
          {expanded && tree(z.id, depth + 1)}
        </div>
      );
    });

  if (zones.isLoading || rooms.isLoading) return <Loading />;
  return (
    <>
      <div className="stat-strip">
        <StatCard icon={<Building2 size={22} />} value={sitesCount} label="Total sites" sub="In your organization" />
        <StatCard icon={<Layers size={22} />} value={allZones.length} label="Buildings / floors" sub="In this site" />
        <StatCard icon={<DoorOpen size={22} />} value={allRooms.length} label="Rooms" sub="In this site" />
        <StatCard icon={<MonitorSmartphone size={22} />} value={devices.data?.items.length ?? site._count?.devices ?? 0} label="Devices" sub="Connected TVs and STBs" />
      </div>

      <div className="pkg-layout sites-layout">
        <div className="card site-panel">
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <span className="site-name">{site.name}</span>
            <span className="badge">{site.code}</span>
            {admin && (
              <span style={{ marginLeft: 'auto' }}>
                <Menu
                  minWidth={200}
                  button={({ onClick }) => (
                    <button type="button" className="icon-btn" title="More" onClick={onClick}>
                      <MoreHorizontal size={18} />
                    </button>
                  )}
                >
                  <button type="button" className="more-item" onClick={() => setAssignFor({ type: 'SITE', id: site.id, label: `${site.name} (whole site)` })}>
                    <PackageIcon size={15} /> Site packages
                  </button>
                  <button type="button" className="more-item" onClick={() => setNewZone({ parentId: null })}>
                    <Plus size={15} /> Add building / floor / area
                  </button>
                </Menu>
              </span>
            )}
          </div>
          <div className="muted row" style={{ gap: 6, fontSize: 13 }}>
            <MapPin size={15} /> {site.timezone}
          </div>
          {admin && <EdgeSetting site={site} />}
          {admin && (
            <button type="button" className="site-link" onClick={() => setAssignFor({ type: 'SITE', id: site.id, label: `${site.name} (whole site)` })}>
              <PackageIcon size={18} /> <span>Site packages</span> <ChevronRight size={16} />
            </button>
          )}
          <h3 style={{ margin: '6px 0 0' }}>Buildings / floors / areas</h3>
          <div className="tree">
            <div className={`tree-row${zoneId === null ? ' on' : ''}`} style={{ paddingLeft: 8 }} onClick={() => setZoneId(null)}>
              <span className="tree-icon">
                <Home size={16} />
              </span>
              <span className="tree-name">All rooms</span>
              <span className="pkg-count">{allRooms.length}</span>
            </div>
            {tree(null, 0)}
          </div>
          {admin && (
            <button className="btn add-zone" onClick={() => setNewZone({ parentId: null })}>
              <Plus size={16} /> Add building / floor / area
            </button>
          )}
        </div>

        <div className="card fill">
          <div className="pkg-head">
            <span className="pkg-head-icon">
              <DoorOpen size={22} />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="row" style={{ gap: 8 }}>
                <span className="pkg-head-title">Rooms</span>
                <span className="pkg-count">{shown.length}</span>
              </span>
              <span className="muted" style={{ fontSize: 13 }}>
                Rooms in {zone ? `${zone.name} · ` : ''}
                {site.name}
              </span>
            </span>
            {admin && (
              <button className="btn" onClick={() => setBulk(true)}>
                <Upload size={16} /> Add rooms
              </button>
            )}
          </div>
          <div className="filters" style={{ marginTop: 14 }}>
            <label className="search">
              <Search size={16} />
              <input placeholder="Search rooms (e.g. 1201)…" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <Select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Room type">
              <option value="all">All types</option>
              {Object.entries(ROOM_KIND).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </div>
          {selected.size > 0 && admin && (
            <div className="bulk-bar">
              <strong>{selected.size} selected</strong>
              <button className="btn small danger" onClick={() => ask(`Delete ${selected.size} rooms? Their TVs stay, without a room.`, () => deleteRooms.mutate([...selected]))}>
                <Trash2 size={14} /> Delete
              </button>
              <button className="icon-btn" style={{ marginLeft: 'auto' }} onClick={() => setSelected(new Set())} title="Clear selection">
                <X size={16} />
              </button>
            </div>
          )}
          {shown.length === 0 ? (
            <Empty>{allRooms.length ? 'No room matches.' : 'No rooms here yet. Use “Add rooms” to add many at once (e.g. 1201-1210).'}</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {admin && (
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          checked={allOnPage}
                          onChange={() =>
                            setSelected((cur) => {
                              const n = new Set(cur);
                              pageIds.forEach((id) => (allOnPage ? n.delete(id) : n.add(id)));
                              return n;
                            })
                          }
                          aria-label="Select this page"
                        />
                      </th>
                    )}
                    <SortTh k="number">Room</SortTh>
                    <SortTh k="kind">Type</SortTh>
                    <SortTh k="devices">Devices</SortTh>
                    <th>Packages</th>
                    <SortTh k="status">Status</SortTh>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const st = stateOf(r);
                    const pk = pkgsOf(r);
                    return (
                      <tr key={r.id}>
                        {admin && (
                          <td>
                            <input
                              type="checkbox"
                              checked={selected.has(r.id)}
                              onChange={() =>
                                setSelected((cur) => {
                                  const n = new Set(cur);
                                  if (n.has(r.id)) n.delete(r.id);
                                  else n.add(r.id);
                                  return n;
                                })
                              }
                              aria-label={`Select room ${r.number}`}
                            />
                          </td>
                        )}
                        <td className="cell-title">{r.number}</td>
                        <td>
                          <span className="row" style={{ gap: 6, flexWrap: 'nowrap', whiteSpace: 'nowrap', color: 'var(--muted)' }}>
                            {r.kind === 'GUEST_ROOM' || r.kind === 'SUITE' ? <BedDouble size={16} /> : <LayoutGrid size={16} />}
                            {ROOM_KIND[r.kind] ?? r.kind}
                          </span>
                        </td>
                        <td>{r._count?.devices ?? 0}</td>
                        <td>
                          <span className="row" style={{ gap: 4 }}>
                            {pk.length ? (
                              pk.map((n) => (
                                <span key={n} className="chip" style={{ color: '#5b21b6', background: '#f1ebff' }}>
                                  {n}
                                </span>
                              ))
                            ) : (
                              <span className="muted" style={{ fontSize: 12.5 }}>From floor / site</span>
                            )}
                          </span>
                        </td>
                        <td>
                          <span className={`state-pill ${st === 'online' ? 'online' : st === 'offline' ? 'disabled' : 'offline'}`}>
                            <span className="state-dot" /> {st === 'online' ? 'Online' : st === 'offline' ? 'Offline' : 'No TV'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {admin && (
                            <Menu
                              minWidth={190}
                              button={({ onClick }) => (
                                <button type="button" className="icon-btn" title="More" onClick={onClick}>
                                  <MoreHorizontal size={18} />
                                </button>
                              )}
                            >
                              <button type="button" className="more-item" onClick={() => setAssignFor({ type: 'ROOM', id: r.id, label: `Room ${r.number}` })}>
                                <PackageIcon size={15} /> Packages
                              </button>
                              <Link className="more-item" to={`/devices/report?room=${r.id}`}>
                                <FileBarChart size={15} /> TV operation report
                              </Link>
                              <button type="button" className="more-item danger-item" onClick={() => ask(`Delete room ${r.number}? Its TVs stay, without a room.`, () => deleteRooms.mutate([r.id]))}>
                                <Trash2 size={15} /> Delete
                              </button>
                            </Menu>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <Pager {...pager} />
          <ErrorBox error={deleteRooms.error ?? deleteZone.error} />
        </div>
      </div>

      {newZone && <ZoneModal siteId={site.id} parentId={newZone.parentId} zones={allZones} onClose={() => setNewZone(null)} onDone={invalidate} />}
      {bulk && <BulkRoomsModal siteId={site.id} zones={allZones} defaultZoneId={zoneId} onClose={() => setBulk(false)} onDone={invalidate} />}
      {assignFor && (
        <Modal title={`Packages · ${assignFor.label}`} onClose={() => setAssignFor(null)}>
          <AssignmentEditor siteId={site.id} scopeType={assignFor.type} scopeId={assignFor.id} />
        </Modal>
      )}
    </>
  );
}

function ZoneModal({ siteId, parentId, zones, onClose, onDone }: { siteId: string; parentId: string | null; zones: Zone[]; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [parent, setParent] = useState(parentId ?? '');
  const [kind, setKind] = useState<Zone['kind']>(parentId ? 'FLOOR' : 'BUILDING');
  const create = useMutation({
    mutationFn: () => api.post(`/sites/${siteId}/zones`, { name, kind, parentId: parent || null }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  return (
    <Modal
      title="Add building / floor / area"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!name || create.isPending} onClick={() => create.mutate()}>
            Add
          </button>
        </>
      }
    >
      <div className="grid cols-2">
        <Field label="Type">
          <Select value={kind} onChange={(e) => setKind(e.target.value as Zone['kind'])}>
            <option value="BUILDING">Building</option>
            <option value="FLOOR">Floor</option>
            <option value="AREA">Area (wing, lobby, spa…)</option>
          </Select>
        </Field>
        <Field label="Inside">
          <Select value={parent} onChange={(e) => setParent(e.target.value)}>
            <option value="">The site itself</option>
            {zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Field label="Name *">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Floor 12" autoFocus />
      </Field>
      <ErrorBox error={create.error} />
    </Modal>
  );
}

/** Paste room numbers or ranges: "1201-1210, 1301, Lobby" */
export function parseRooms(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split(/[\n,;]+/)) {
    const part = raw.trim();
    if (!part) continue;
    const range = /^(\d+)\s*-\s*(\d+)$/.exec(part);
    if (range) {
      const [a, b] = [Number(range[1]), Number(range[2])];
      for (let n = Math.min(a, b); n <= Math.max(a, b) && out.length < 5000; n++) out.push(String(n));
    } else out.push(part);
  }
  return [...new Set(out)];
}

function BulkRoomsModal({
  siteId,
  zones,
  defaultZoneId,
  onClose,
  onDone,
}: {
  siteId: string;
  zones: Zone[];
  defaultZoneId: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [text, setText] = useState('');
  const [zoneId, setZoneId] = useState(defaultZoneId ?? '');
  const [kind, setKind] = useState('GUEST_ROOM');
  const numbers = parseRooms(text);
  const create = useMutation({
    mutationFn: () =>
      api.post<{ created: number; skipped: number }>(`/sites/${siteId}/rooms/bulk`, {
        rooms: numbers.map((number) => ({ number, kind, zoneId: zoneId || null })),
      }),
    onSuccess: () => onDone(),
  });
  return (
    <Modal
      title="Add rooms"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Close</button>
          <button className="btn primary" disabled={!numbers.length || create.isPending} onClick={() => create.mutate()}>
            Add {numbers.length} rooms
          </button>
        </>
      }
    >
      <Field label="Room numbers" hint="One per line or comma-separated. Ranges allowed: 1201-1210. Existing rooms are skipped.">
        <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={'1201-1210\n1301-1310\nLobby'} autoFocus />
      </Field>
      <div className="grid cols-2">
        <Field label="Location">
          <Select value={zoneId} onChange={(e) => setZoneId(e.target.value)}>
            <option value="">Site (no building/floor)</option>
            {zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Type">
          <Select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="GUEST_ROOM">Guest room</option>
            <option value="SUITE">Suite</option>
            <option value="LOBBY">Lobby</option>
            <option value="PUBLIC">Public area</option>
            <option value="STAFF">Staff</option>
            <option value="OTHER">Other</option>
          </Select>
        </Field>
      </div>
      {create.data && (
        <div className="badge ok">
          Added {create.data.created}, skipped {create.data.skipped} existing
        </div>
      )}
      <ErrorBox error={create.error} />
    </Modal>
  );
}

/**
 * Hotel edge server (hybrid): the TVs of this hotel use it instead of the cloud and keep working through
 * internet outages. Samsung Tizen TVs move to it by themselves; LG, older Samsung and Android boxes are
 * pointed at it in their own settings.
 */
function EdgeSetting({ site }: { site: Site }) {
  const qc = useQueryClient();
  const current = (site.settings?.edgeUrl as string | null | undefined) ?? '';
  const [value, setValue] = useState(current);
  useEffect(() => setValue(current), [current]);
  const save = useMutation({
    mutationFn: (body: { edgeUrl?: string | null; tvInternet?: boolean }) => api.patch(`/sites/${site.id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });
  const key = site.settings?.edgeKey as string | undefined;
  const tvInternet = site.settings?.tvInternet !== false;
  const dirty = value.trim() !== current;
  return (
    <div style={{ fontSize: 13, margin: '4px 0 2px' }}>
      <div className="muted" style={{ marginBottom: 4 }}>Hotel edge server (optional)</div>
      <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
        <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="http://192.168.1.10" style={{ maxWidth: 210 }} />
        {dirty && (
          <button type="button" className="primary" onClick={() => save.mutate({ edgeUrl: value.trim() || null })} disabled={save.isPending}>
            Save
          </button>
        )}
      </div>
      {current && (
        <>
          <label className="row" style={{ gap: 6, marginTop: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={!tvInternet} onChange={(e) => save.mutate({ tvInternet: !e.target.checked })} />
            <span>TVs have no internet: internet channels come through the edge server</span>
          </label>
          {key && (
            <div className="muted" style={{ marginTop: 6 }}>
              Edge key (for the installer): <code style={{ userSelect: 'all' }}>{key}</code>
            </div>
          )}
        </>
      )}
      {save.error && <div className="error" style={{ marginTop: 4 }}>{(save.error as Error).message}</div>}
    </div>
  );
}
